import type { MantineTheme } from '@mantine/core';
import {
	ActionIcon,
	Box,
	createStyles,
	Flex,
	Indicator,
	Loader,
} from '@mantine/core';
import { useNavigateHandler } from '@repo/common/hooks/useNavigate';
import { Badge, Icon, Text } from '@repo/foundations';
import { transition } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import type { MouseEvent, ReactNode } from 'react';
import { Suspense, useCallback, useState } from 'react';
import { compactNumber } from '../../../utils/numberUtils';
import SidebarTooltipWrapper from './SidebarTooltipWrapper';

export interface Link {
	id: string;
	label: string | JSX.Element;
	icon: JSX.Element;
	href: string[];
	matchExact?: boolean;
	show: boolean;
	collapsable?: boolean;
	collapsedComponent?: ReactNode;
	showCount?: boolean;
	count?: number;
	target?: string;
	underTeam?: boolean;
}

interface SideBarLinkProps {
	link: Link;
	selected: boolean;
}

interface SideBarLinkStyleProps {
	selected: boolean;
	collapsableOpened: boolean;
}

const useStyles = createStyles(
	(
		theme: MantineTheme,
		{ selected, collapsableOpened }: SideBarLinkStyleProps
	) => ({
		indicator: {
			paddingTop: theme.other.space[0.25],
		},
		sidebarItem: {
			height: theme.other.space[7],
			width: '100%',

			cursor: 'pointer',
			borderRadius: theme.other.borderRadius.sm,
			color: theme.other.getColor('text/primary/default'),
			backgroundColor: selected
				? theme.other.getColor('fill/transparent/selected')
				: 'transparent',
			':hover': {
				backgroundColor: selected
					? theme.other.getColor('fill/transparent/selected')
					: theme.other.getColor('fill/transparent/hover'),
			},
			':active': {
				backgroundColor: theme.other.getColor('fill/transparent/active'),
			},
		},
		chevron: {
			transform: collapsableOpened ? 'rotate(90deg)' : 'none',
			...transition('duration-300', 'easeInOut')('transform'),
		},
		chevronButton: {
			padding: 0,
			width: theme.other.space[7],
			height: theme.other.space[7],
			color: theme.other.getColor('icon/secondary/default'),
			':hover': {
				color: theme.other.getColor('icon/primary/default'),
				backgroundColor: theme.other.getColor('fill/transparent/hover'),
			},
		},
		linkText: {
			color: theme.other.getColor('text/primary/default'),
			whiteSpace: 'nowrap',
		},
		badgeRoot: {
			margin: 0,
			marginRight: theme.other.space[1],
			borderRadius: theme.other.borderRadius.sm,
		},
	})
);

function SideBarLink({ link, selected }: SideBarLinkProps) {
	const [collapsableOpened, setCollapsableOpened] = useState(false);
	const { label, icon, href } = link;

	const { classes, theme } = useStyles({
		selected,
		collapsableOpened,
	});

	const navigateHandler = useNavigateHandler();

	const handleNavigate = (event: MouseEvent) => {
		// If this is a collapsible item, just toggle the dropdown - don't navigate
		if (link.collapsable) {
			if (link.label === 'Secoda AI') {
				setCollapsableOpened(!collapsableOpened);
				return;
			}
		}

		// For non-collapsible items, navigate normally
		navigateHandler(event)(href[0]);
	};

	const handledCollapsable = useCallback(
		(event: MouseEvent) => {
			event.stopPropagation();
			setCollapsableOpened(!collapsableOpened);
		},
		[collapsableOpened]
	);

	const CollapsedComponent = link.collapsedComponent;
	const showCollapsableComponent = CollapsedComponent;

	const shouldShowCountBadge: boolean =
		(link.showCount && (link?.count || 0) > 0) || false;

	const shouldShowChevron: boolean = Boolean(
		link.collapsable || (link.underTeam && (link?.count || 0) > 0)
	);

	if (!link.show) {
		return null;
	}

	return (
		<Box w={'100%'}>
			<Indicator
				size={10}
				offset={10}
				className={classes.indicator}
				disabled={!link.showCount || !link.count || shouldShowCountBadge}
				color={theme.other.getColor('icon/primary/default')}
				label={link?.count && link.count > 99 ? '99+' : undefined}
			>
				<Flex
					className={classes.sidebarItem}
					role="button"
					onClick={handleNavigate}
				>
					<SidebarTooltipWrapper label={label} showTooltip={false}>
						<Flex w="100%" justify={'space-between'} align="center">
							<Flex justify="center" align="center">
								{/* Sub-item chevron (for items under teams with counts) */}
								{shouldShowChevron &&
									link.underTeam &&
									(link?.count || 0) > 0 && (
										<ActionIcon
											onClick={handledCollapsable}
											data-testid={`sidebar-link-${label}-collapsable`}
											className={classes.chevronButton}
										>
											<Icon name="chevronRight" className={classes.chevron} />
										</ActionIcon>
									)}
								{/* Space for sub-items when no chevron */}
								{link.underTeam &&
									!(shouldShowChevron && (link?.count || 0) > 0) && (
										<Box w={theme.other.space[7]} />
									)}
								{/* Space for non-team, non-collapsible items */}
								{!link.underTeam && !link.collapsable && (
									<Box w={theme.other.space[1]} />
								)}
								<Flex align="center">
									<Box pr={`${theme.other.space[1.5]}px`}>{icon}</Box>
									<Text
										size="sm"
										weight="semibold"
										className={classes.linkText}
									>
										{label}
									</Text>
								</Flex>
							</Flex>
							{shouldShowCountBadge && (
								<Badge
									className={classes.badgeRoot}
									backgroundColor="fill/transparent-secondary/default"
									color="text/primary/default"
									size="xs"
								>
									{link?.count && link.count > 99
										? '99+'
										: compactNumber(link?.count || 0, 1)}
								</Badge>
							)}
							{/* Main collapsible item chevron (like "Secoda AI") */}
							{shouldShowChevron && link.collapsable && !link.underTeam && (
								<ActionIcon
									onClick={handledCollapsable}
									data-testid={`sidebar-link-${label}-collapsable`}
									className={classes.chevronButton}
								>
									<Icon name="chevronRight" className={classes.chevron} />
								</ActionIcon>
							)}
						</Flex>
					</SidebarTooltipWrapper>
				</Flex>
				{showCollapsableComponent && (
					<Box
						sx={{
							display: collapsableOpened ? 'block' : 'none',
						}}
					>
						<Suspense fallback={<Loader my={8} w="100%" size="xs" />}>
							{CollapsedComponent}
						</Suspense>
					</Box>
				)}
			</Indicator>
		</Box>
	);
}

const ObservedSideBarLink = observer(SideBarLink);
export default ObservedSideBarLink;
